/* eslint-env browser */

import { useEffect } from 'react';
import { Platform } from 'react-native';
import {
  WEB_SESSION_ACTIVITY_THROTTLE_MS,
  WEB_SESSION_EXPIRED_KEY,
  WEB_SESSION_IDLE_TIMEOUT_MS,
  WEB_SESSION_LAST_ACTIVITY_KEY,
  WEB_SESSION_MAX_DURATION_MS,
  WEB_SESSION_STARTED_AT_KEY,
} from '../constants/WebSession';

type WebSessionControllerProps = {
  onExpire: () => void;
};

const getTimestamp = (key: string) => Number(window.localStorage.getItem(key)) || 0;

export default function WebSessionController({ onExpire }: WebSessionControllerProps) {
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;

    let idleTimeout: ReturnType<typeof setTimeout>;
    let maximumDurationTimeout: ReturnType<typeof setTimeout>;
    let expired = false;
    let lastPublishedActivity = getTimestamp(WEB_SESSION_LAST_ACTIVITY_KEY);

    const expire = () => {
      if (expired) return;
      expired = true;
      window.localStorage.setItem(WEB_SESSION_EXPIRED_KEY, String(Date.now()));
      onExpire();
    };

    const scheduleExpiry = () => {
      clearTimeout(idleTimeout);
      clearTimeout(maximumDurationTimeout);

      const now = Date.now();
      const sessionStartedAt = getTimestamp(WEB_SESSION_STARTED_AT_KEY) || now;
      const lastActivityAt = getTimestamp(WEB_SESSION_LAST_ACTIVITY_KEY) || now;

      if (!getTimestamp(WEB_SESSION_STARTED_AT_KEY)) {
        window.localStorage.setItem(WEB_SESSION_STARTED_AT_KEY, String(sessionStartedAt));
      }
      if (!getTimestamp(WEB_SESSION_LAST_ACTIVITY_KEY)) {
        window.localStorage.setItem(WEB_SESSION_LAST_ACTIVITY_KEY, String(lastActivityAt));
      }

      const idleRemaining = WEB_SESSION_IDLE_TIMEOUT_MS - (now - lastActivityAt);
      const maximumDurationRemaining = WEB_SESSION_MAX_DURATION_MS - (now - sessionStartedAt);

      if (idleRemaining <= 0 || maximumDurationRemaining <= 0) {
        expire();
        return;
      }

      idleTimeout = setTimeout(expire, idleRemaining);
      maximumDurationTimeout = setTimeout(expire, maximumDurationRemaining);
    };

    const recordActivity = () => {
      if (expired) return;

      const now = Date.now();
      clearTimeout(idleTimeout);
      idleTimeout = setTimeout(expire, WEB_SESSION_IDLE_TIMEOUT_MS);

      if (now - lastPublishedActivity >= WEB_SESSION_ACTIVITY_THROTTLE_MS) {
        lastPublishedActivity = now;
        window.localStorage.setItem(WEB_SESSION_LAST_ACTIVITY_KEY, String(now));
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === WEB_SESSION_EXPIRED_KEY && event.newValue) {
        expire();
        return;
      }
      if (event.key === WEB_SESSION_LAST_ACTIVITY_KEY || event.key === WEB_SESSION_STARTED_AT_KEY) {
        scheduleExpiry();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') recordActivity();
    };

    scheduleExpiry();
    const activityEvents = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'focus'];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, recordActivity, { passive: true });
    });
    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(idleTimeout);
      clearTimeout(maximumDurationTimeout);
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, recordActivity));
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onExpire]);

  return null;
}
